import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/auth/google-login';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GoogleAuthInput = paths[url]['post']['requestBody']['content']['application/json'];
export type GoogleAuthSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

export const useGoogleAuthService = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { setLogin, setRegister, setFirstStep } = useData();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const sessionId = getSessionId();

    const handleGoogleLogin = async (token: string) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ token: token, session_id: sessionId }),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.message || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GoogleAuthSuccessResponse = await response.json();

            if (data.data?.access_token || data.data?.redirect) {
                setLogin(data);
            } else {
                setRegister(true);

                setFirstStep({
                    data: {
                        step_1: {
                            name: data.data?.user?.name || '',
                            email: data.data?.user?.email || '',
                            provider: 'google',
                        },
                    },
                });
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { handleGoogleLogin, loading, error, lastResponseTime };
};
